import { useState } from "react"

import {
  getGetAccountEmailQueryKey,
  useGetAccountEmail,
  usePostAccountEmail,
  usePutAccountEmail,
} from "@boundless/bl-api-client/allauth/account"
import { PostAccountEmailBody as EmailFormSchema } from "@boundless/bl-api-client/allauth/allauth.zod"
import { usePostAuthEmailVerify } from "@boundless/bl-api-client/allauth/auth"
import { getCommonUserRetrieveQueryKey } from "@boundless/bl-api-client/common/services"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { LoaderIcon, MailIcon } from "lucide-react"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { CodeEntryForm } from "@/components/account/CodeEntryForm"
import { FormInput } from "@/components/form-elements/FormInput"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Form } from "@/components/ui/form"
import { isEmailAddressTakenError } from "@/lib/account/utils"
import { getAuthClient } from "@/lib/clients"
import { applyErrorsToForm } from "@/lib/forms/utils"

export function ChangeEmailDialog() {
  const client = getAuthClient()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [pendingEmail, setPendingEmail] = useState<string | null>(null)
  const [useDifferentEmail, setUseDifferentEmail] = useState(false)
  const [verificationError, setVerificationError] = useState<string | null>(
    null,
  )

  const form = useForm<z.infer<typeof EmailFormSchema>>({
    resolver: zodResolver(EmailFormSchema),
    defaultValues: {
      email: "",
    },
  })

  const { data: accountEmails, isLoading: isLoadingAccountEmails } =
    useGetAccountEmail(client, {
      query: { enabled: open },
    })

  const serverPendingEmail = accountEmails?.data.find(
    (address) => !address.verified,
  )?.email
  const codeEmail =
    pendingEmail ?? (useDifferentEmail ? null : serverPendingEmail)
  const isCodeStep = !!codeEmail

  const {
    mutate: changeEmail,
    isPending: isChangingEmail,
    reset: resetChangeEmail,
  } = usePostAccountEmail({
    mutation: {
      onSuccess: (response, variables) => {
        const submittedEmail = variables.data.email
        const pendingAddress = response.data.find(
          (address) =>
            !address.verified &&
            address.email.toLowerCase() === submittedEmail.toLowerCase(),
        )
        const email = pendingAddress?.email ?? submittedEmail

        queryClient.setQueryData(getGetAccountEmailQueryKey(client), response)
        setPendingEmail(email)
        setUseDifferentEmail(false)
        setVerificationError(null)
        form.reset()
      },
      onError: (error) => {
        applyErrorsToForm(form.setError, error)
      },
    },
  })

  const { mutate: resendCode, reset: resetResend } = usePutAccountEmail({
    mutation: {
      onError: () => {
        setVerificationError(
          "We couldn't resend the code right now. Please try again.",
        )
      },
    },
  })

  const {
    mutate: verifyEmail,
    isPending: isVerifying,
    reset: resetVerification,
  } = usePostAuthEmailVerify({
    mutation: {
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getCommonUserRetrieveQueryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: getGetAccountEmailQueryKey(client),
          }),
        ])
        handleOpenChange(false)
      },
      onError: (error) => {
        if (isEmailAddressTakenError(error)) {
          setVerificationError(
            "That email address is already associated with another account. Use a different email address.",
          )
          return
        }
        setVerificationError(
          "That code is invalid or has expired. Please try again.",
        )
      },
    },
  })

  function handleOpenChange(newOpen: boolean) {
    setOpen(newOpen)
    if (!newOpen) {
      form.reset()
      setPendingEmail(null)
      setUseDifferentEmail(false)
      setVerificationError(null)
      resetChangeEmail()
      resetResend()
      resetVerification()
    }
  }

  function chooseDifferentEmail() {
    form.reset()
    setPendingEmail(null)
    setUseDifferentEmail(true)
    setVerificationError(null)
    resetVerification()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-40">
          <MailIcon className="mr-2 h-4 w-4" />
          Change Email
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {!isCodeStep ? "Change Email Address" : "Confirm Email Address"}
          </DialogTitle>
          <DialogDescription>
            {!isCodeStep
              ? "Enter your new email address. We'll send a verification code to confirm the change."
              : "Enter the verification code sent to your new email address."}
          </DialogDescription>
        </DialogHeader>

        {isLoadingAccountEmails ? (
          <div className="flex justify-center py-8">
            <LoaderIcon className="h-6 w-6 animate-spin" />
          </div>
        ) : codeEmail ? (
          <div className="space-y-2">
            <CodeEntryForm
              email={codeEmail}
              isSubmitting={isVerifying}
              error={verificationError}
              submitLabel="Confirm email"
              onSubmit={(code) => {
                setVerificationError(null)
                verifyEmail({ client, data: { key: code } })
              }}
              onResend={() => {
                setVerificationError(null)
                resendCode({ client, data: { email: codeEmail } })
              }}
            />
            <div className="text-center">
              <Button
                type="button"
                variant="link"
                className="p-0 text-neutral-600 hover:text-neutral-800"
                onClick={chooseDifferentEmail}
              >
                Wrong email? Use a different one
              </Button>
            </div>
          </div>
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((data) =>
                changeEmail({ client, data }),
              )}
              className="space-y-4"
            >
              <FormInput
                name="email"
                placeholder="New Email Address"
                type="email"
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isChangingEmail}>
                  {isChangingEmail ? (
                    <>
                      <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />
                      Changing...
                    </>
                  ) : (
                    "Change Email"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
